import React from 'react';
import { Link } from 'react-router-dom';
import { ClassSummary } from '../../types/models';

interface Props {
  event: ClassSummary;
}

const STATUS_COLORS: Record<string, string> = {
  Planned: 'bg-blue-100 text-blue-700',
  Active: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
  Completed: 'bg-gray-100 text-gray-600',
};

export const ClassCard: React.FC<Props> = ({ event }) => {
  const date = new Date(event.startDateTime);
  const isFull = event.availableSpots === 0;
  const isRegistered = event.userRegistration?.isRegistered;
  const isWaitlisted = event.userRegistration?.isWaitlisted;

  return (
    <Link to={`/classes/${event.eventId}`} className="block">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.publicDescription}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[event.status] ?? ''}`}>
            {event.classType}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
          <span>
            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            {' · '}
            {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
          <span>{event.duration} min</span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          {event.status === 'Planned' ? (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">Draft</span>
          ) : (
            <span className={`text-sm font-medium ${isFull ? 'text-red-500' : 'text-green-600'}`}>
              {isFull ? 'Full' : `${event.availableSpots} spot${event.availableSpots === 1 ? '' : 's'} left`}
            </span>
          )}
          {isRegistered && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Registered</span>
          )}
          {isWaitlisted && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
              Waitlisted #{event.userRegistration?.waitlistPosition}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
